import type { ElementType } from 'react';

import type { PolymorphicPropsWithRef } from '@/shared/types';

import { convertStyleProps } from './styleConvertor';
import type { OpdsStyleProps } from './styleProps';
import { stylePropsMap } from './styleProps';

export type BoxProps = OpdsStyleProps & {
  children?: React.ReactNode;
};

export type PolymorphicBoxProps<C extends ElementType = 'div'> = PolymorphicPropsWithRef<C, BoxProps>;

const separateProps = (props: BoxProps) => {
  const styleProps: Record<string, unknown> = {};
  const htmlProps: Record<string, unknown> = {};

  Object.entries(props).forEach(([key, value]) => {
    if (key in stylePropsMap) {
      styleProps[key] = value;
    } else {
      htmlProps[key] = value;
    }
  });

  return {
    styleProps: styleProps as Partial<OpdsStyleProps>,
    htmlProps: htmlProps as Partial<Omit<BoxProps, keyof OpdsStyleProps>>,
  };
};

const Box = <C extends ElementType = 'div'>(props: PolymorphicBoxProps<C>) => {
  const { as: Component = 'div', ref, style, children, ...restProps } = props;

  const { styleProps, htmlProps } = separateProps(restProps);

  const convertedStyles = convertStyleProps(styleProps);

  const mergedStyles = {
    ...convertedStyles,
    ...style,
  };

  return (
    <Component ref={ref} style={mergedStyles} {...htmlProps}>
      {children}
    </Component>
  );
};

Box.displayName = 'Box';

export default Box;
